// NPM Modules
import { writable } from 'svelte/store';
import collect from 'collect.js';
// Models
import Race from '@/Models/Race';
// Stores
import { mergeCollectionStore, mergeModelDataInCollectionStore } from '@/Stores/StoreHelpers.js';
import SelectionStore from '@/Stores/Racing/SelectionStore.js';
import PriceStore from '@/Stores/Racing/PriceStore.js';
import ResultStore from '@/Stores/Racing/ResultStore.js';
import RaceBetTypeStore from '@/Stores/Racing/RaceBetTypeStore.js';
import { showBetsForSelectionId } from '@/Stores/Store.js';
// Constants
import config from '@/config.js';
import { EventStatus } from '@/Constants/Event.js';
// Services
import { notify } from '@/Services/Notify.js';
import Selection from '@/Models/Selection.js';

/**
 * Our Store can be used 2 ways in svelte components
 * 1) ($ prefix) $StoreName to reference writable store collection with all collect.js methods available
 * 2) (Direct) StoreName.method() to call a method on the store
 */
const init = () => {
	let _store;

	const { subscribe, set, update } = writable(collect());

	subscribe((current) => (_store = current));

	const methods = {
		/**
		 * ----------------- MODEL/API METHODS -----------------
		 */

		/**
		 * getRaceWithDetails with selections.prices & result
		 * @param {*} id
		 * @param {string} meetingId
		 * @param {boolean} resetSelections
		 */
		getRaceWithDetails(id, meetingId, resetSelections = false) {
			if (!config.RACING_ENABLED || !id) return;

			const race = new Race();
			let allSelections = collect();
			let allPrices = collect();
			// force an update of selections to remove any stale data
			if (resetSelections) {
				showBetsForSelectionId.set(false);
				SelectionStore.set(allSelections);
			}
			return race
				.with(['selections.prices', 'result', 'promotions'])
				.find(id)
				.then((result) => {
					// store the meeting_id on the Race model
					// result.meeting_id = result.meeting.id;
					result.meeting_id = meetingId && meetingId;

					// extract selections to Selections collection
					result.selections.data.map((selection) => {
						selection.race_id = id;
						delete selection.race;

						// extract nested prices for this selection to Prices collection
						selection.prices.data.map((price) => {
							price.selection_id = selection.id;
							delete price.race;

							allPrices.push(price);
						});

						delete selection.prices;

						allSelections.push(selection);
					});

					delete result.selections;

					// extract Results for this race to Results store
					ResultStore.set(result.result);
					delete result.result;

					// create all our new collection stores
					SelectionStore.set(allSelections);
					PriceStore.set(allPrices);

					// mergeCollectionStore(MeetingStore, collect([result.meeting]));

					// delete result.meeting;

					mergeCollectionStore(this, collect([result]));
				})
				.catch((e) => {
					console.error(e);
				});
		},

		/**
		 * fetch Race with Bet Types and update our store collections
		 * @param {*} raceId
		 * @param {*} meetingId
		 */
		getRaceWithBetTypes(raceId, meetingId) {
			if (!config.RACING_ENABLED || !raceId) return;
			const race = new Race();
			let allRaceBetTypes = collect();

			return race
				.with(['bet_types'])
				.find(raceId)
				.then((result) => {
					result.bet_types.data.map((betType) => {
						betType.race_id = raceId.toString();
						allRaceBetTypes.push(betType);
					});
					RaceBetTypeStore.set(allRaceBetTypes);

					delete result.bet_types;
					result.meeting_id = meetingId;
					mergeCollectionStore(this, collect([result]));
				})
				.catch((e) => {
					console.error(e);
				});
		},

		getRacingSelectionWithPrices(id, raceId) {
			if (!config.RACING_ENABLED || !id) return;

			const selection = new Selection();
			let allSelections = collect();
			let allPrices = collect();

			return selection
				.with(['prices'])
				.find(id)
				.then((result) => {
					result.race_id = raceId;
					delete result.race;

					// extract nested prices for this selection to Prices collection
					result.prices.data.map((price) => {
						price.selection_id = result.id;
						delete price.race;

						allPrices.push(price);
					});

					delete result.prices;

					allSelections.push(result);

					// create all our new collection stores
					SelectionStore.set(allSelections);
					PriceStore.set(allPrices);
				})
				.catch((e) => {
					console.error(e);
				});
		},

		updateRaceClassification(raceId, classificationId) {
			if (!config.RACING_ENABLED) return;

			let race = _store.firstWhere('id', raceId);
			return race
				.update({
					classification_id: classificationId
				})
				.then((results) => {
					this.getRaceWithBetTypes(raceId, race.meeting_id);
					return results;
				});
		},

		updateRaceAttribute(raceId, attribute, value) {
			if (!config.RACING_ENABLED) return;

			return _store
				.firstWhere('id', raceId)
				.update({
					[attribute]: value
				})
				.then((results) => {
					// keep our local collection in sync
					update((races) => {
						return races.each((race) => {
							if (race.id === raceId && attribute in race) {
								race[attribute] = value;
							}
							return race;
						});
					});
					return results;
				});
		},

		updatePromotion(raceId, data) {
			if (!config.RACING_ENABLED) return;

			let race = _store.firstWhere('id', raceId);
			return race.updatePromotion(data).then((results) => {
				this.getRaceWithDetails(raceId, race.meeting_id);
				return results;
			});
		},

		removePromotion(raceId, data) {
			if (!config.RACING_ENABLED) return;

			let race = _store.firstWhere('id', raceId);
			return race.removePromotion(data).then((results) => {
				this.getRaceWithDetails(raceId, race.meeting_id);
				return results;
			});
		},

		updateManualReview(raceId, data) {
			if (!config.RACING_ENABLED) return;

			return _store
				.firstWhere('id', raceId)
				.update(data)
				.then((results) => {
					// keep our local collection in sync
					update((races) => {
						return races.each((race) => {
							if (race.id === raceId) {
								race.in_review = data.in_review;
							}
							return race;
						});
					});
					return results;
				});
		},

		updateRaceStatus(raceId, statusId) {
			if (!config.RACING_ENABLED) return;

			let race = _store.firstWhere('id', raceId);
			return race
				.update({
					status_id: statusId
				})
				.then((results) => {
					this.getRaceWithDetails(raceId, race.meeting_id);
					return results;
				});
		},

		updateManualResult(raceId, data) {
			if (!config.RACING_ENABLED) return;

			return _store
				.firstWhere('id', raceId)
				.update(data)
				.then((results) => {
					// keep our local collection in sync
					update((races) => {
						return races.each((race) => {
							if (race.id === raceId) {
								race.manual_result = data.manual_result;
								race.in_review = data.in_review;
							}
							return race;
						});
					});
					return results;
				});
		},

		updateDivisor(raceId, divisor) {
			if (!config.RACING_ENABLED) return;

			let race = _store.firstWhere('id', raceId);
			return race
				.update({
					fixed_place_divisor: divisor
				})
				.then((results) => {
					this.getRaceWithDetails(raceId, race.meeting_id);
					return results;
				});
		},

		applyRaceUpdates(data) {
			if (data && data.id && _store.firstWhere('id', data.id.toString())) {
				mergeModelDataInCollectionStore(data, this);
			}
		},

		applyBatchRaceUpdates(data) {
			if (!data) return;

			let races = collect(data);
			update((items) => {
				return items.each((item) => {
					let race = races.firstWhere('id', parseInt(item.id));
					if (race) {
						delete race.id;
						item = Object.assign(item, race);
					}
					return item;
				});
			});
		},

		markRaceUnfinalised(raceId) {
			if (!config.RACING_ENABLED) return;

			let race = _store.firstWhere('id', raceId);
			return race
				.unfinalise()
				.then((results) => {
					this.getRaceWithDetails(raceId, race.meeting_id);
					notify({
						type: 'success',
						title: 'Race unfinalised',
						message: results.meta.message,
						timeout: 3
					});
					return results;
				})
				.catch((e) => {
					notify({
						type: 'warning',
						title: 'Race unfinalising error!',
						message: (e.error && e.error[0]) || 'Issue unfinalising race',
						timeout: 5
					});
				});
		},

		/**
		 * ----------------- STORE METHODS -----------------
		 */

		/**
		 * getNextRaceId for a meeting id
		 * @param {string} meetingId
		 */
		getNextRaceId(meetingId) {
			let nextRace = _store
				.where('meeting_id', meetingId)
				.whereIn('status.id', [EventStatus.Open, EventStatus.ReOpened])
				.sortBy('start_time')
				.first();

			let firstRace = _store.where('meeting_id', meetingId).sortBy('start_time').first();

			if (nextRace) {
				return nextRace.id;
			} else if (firstRace) {
				return firstRace.id;
			}
		},

		getMeetingIdForNextRace() {
			let nextRace = _store.whereIn('status.id', [EventStatus.Open, EventStatus.ReOpened]).sortBy('start_time').first();

			if (nextRace) {
				return nextRace.meeting_id;
			}

			let firstRace = _store.sortBy('start_time').first();

			return firstRace ? firstRace.meeting_id : undefined;
		}
	};

	return {
		subscribe,
		update,
		set,
		...methods
	};
};

export default init();
